import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { useClients } from '~/api/clients';
import { useConditions, useCustomConditions } from '~/api/conditions';
import { useUser } from '~/api/user';

export const useDefaultBrandId = (brandId: number | null): number | null => {
  const {
    data: { brands },
  } = useUser();

  return (
    brands.find(({ id }) => id === brandId)?.id ??
    brands.find(({ isFavorite }) => isFavorite)?.id ??
    brands.find(({ isPrimary }) => isPrimary)?.id ??
    null
  );
};

export const useDefaultNumberingSeriesId = (numberingSeriesId: number | null, type: 'invoice' | 'creditnote'): number | null => {
  const {
    data: { numberingSeries },
  } = useUser();

  if (numberingSeriesId === null) return null;

  return (
    numberingSeries.find(({ id }) => id === numberingSeriesId)?.id ||
    numberingSeries.find(({ enabledForCreditNotes, enabledForInvoices }) =>
      type === 'invoice' ? enabledForInvoices : enabledForCreditNotes,
    )?.id ||
    null
  );
};

export const useDefaultClientAndContactId = (
  clientId: number | null,
  contactId: number | null,
): { clientId: number; contactId: number } | { clientId: null; contactId: null } => {
  const { data: clients } = useClients();

  // Get the id of the newly created contact (if any), passed via location state
  const location = useLocation();
  const newContactId = (location.state?.contactId ?? null) as number | null;

  const client =
    clients.find(({ contacts }) => contacts.filter(({ deleted }) => !deleted).some(({ id }) => id === Number(newContactId))) ??
    clients.find(({ id }) => id === clientId) ??
    null;

  const contacts = client?.contacts.filter(({ deleted }) => !deleted) ?? [];
  const contact =
    contacts.find(({ id }) => id === newContactId) ??
    contacts.find(({ id }) => id === contactId) ??
    (contacts.length === 1 ? contacts[0] : null);

  return client !== null && contact !== null
    ? {
        clientId: client.id,
        contactId: contact.id,
      }
    : {
        clientId: null,
        contactId: null,
      };
};

export const useDefaultCondition = (conditionId: number | null): { id: number; type: 'condition' } | null => {
  const { data: conditions } = useConditions();

  if (conditionId === null) return null;

  const condition = conditions.find(({ id }) => id === conditionId) ?? null;

  return condition !== null ? { id: condition.id, type: 'condition' } : null;
};

export const useDefaultCustomCondition = (customConditionId: number | null): { id: number; type: 'customCondition' } | null => {
  const { data: customConditions } = useCustomConditions();

  if (customConditionId === null) return null;

  const customCondition = customConditions.find(({ id }) => id === customConditionId) ?? null;

  return customCondition !== null ? { id: customCondition.id, type: 'customCondition' } : null;
};

export const useDefaultCustomAgreement = (
  customAgreementId: number | null,
  contactId: number | null,
): { id: number; type: 'customAgreement' } | null => {
  const { data: clients } = useClients();

  const contact =
    clients
      .flatMap(({ contacts }) => contacts)
      .filter(({ deleted }) => !deleted)
      .find(({ id }) => id === contactId) ?? null;

  if (contact === null || contact.customAgreements.length === 0) return null;

  const customAgreement = contact.customAgreements.find(({ id }) => id === customAgreementId) ?? contact.customAgreements[0];

  return customAgreement !== null ? { id: customAgreement.id, type: 'customAgreement' } : null;
};

export const useDefaultSpecialConditions = (
  specialConditions: string | null,
  contactId: number | null,
  customAgreementId: number | null,
): string => {
  const { data: clients } = useClients();

  const { t } = useTranslation(['invoices']);

  if (customAgreementId === null) return specialConditions ?? '';

  const contact =
    clients
      .flatMap(({ contacts }) => contacts)
      .filter(({ deleted }) => !deleted)
      .find(({ id }) => id === contactId) ?? null;
  const customAgreement = contact?.customAgreements.find(({ id }) => id === customAgreementId) ?? null;

  return contact !== null && customAgreement !== null
    ? t('invoices:fields.specialConditions.custom', { date: dayjs(customAgreement.date).format('DD-MM-YYYY'), lng: contact.language })
    : (specialConditions ?? '');
};
